import { Injectable } from "@angular/core";
import { CesiumMapService, CesiumPointerManagerService, DtmCesiumEvent, PickOptions } from "@dtm-frontend/shared/map/cesium";
import { Feature, Geometry } from "@turf/helpers";
import { Cartesian2 } from "cesium";
import { BehaviorSubject, switchMap } from "rxjs";
import { ActiveRestrictionsLayerFeatureData } from "../../models/active-restrictions-map-layer.models";
import { RestrictionType } from "../../models/flight-zone-shared.models";

type ActiveRestrictionsLayerPickFeatureResponse = Feature<
    Geometry,
    {
        color: string;
        created: string;
        designator: string;
        end_time: string;
        geozone: string;
        is_static: boolean;
        lower_limit: number;
        start_time: string;
        type: RestrictionType;
        upper_limit: number;
        version: number;
    }
>[];

@Injectable()
export class ActiveRestrictionsLayerFeaturePickerService {
    private readonly isFeaturesDataLoadingSubject = new BehaviorSubject<boolean>(false);
    public readonly isFeaturesDataLoading$ = this.isFeaturesDataLoadingSubject.asObservable();
    public readonly pickedFeaturesData$ = this.cesiumPointerManager
        .addEventHandler({
            event: DtmCesiumEvent.LEFT_CLICK,
            pick: PickOptions.NO_PICK,
        })
        .pipe(
            switchMap(async ({ movement: { endPosition } }) => {
                const viewer = this.cesiumService.getViewer();
                const ray = this.cesiumService.getViewer().camera?.getPickRay(endPosition as Cartesian2);

                if (!ray) {
                    return [];
                }

                this.isFeaturesDataLoadingSubject.next(true);
                const features = await viewer.imageryLayers.pickImageryLayerFeatures(ray, viewer.scene);
                this.isFeaturesDataLoadingSubject.next(false);

                return this.mapFeaturesResponseToFeatureData(features as unknown as ActiveRestrictionsLayerPickFeatureResponse);
            })
        );

    constructor(private readonly cesiumService: CesiumMapService, private readonly cesiumPointerManager: CesiumPointerManagerService) {}

    private mapFeaturesResponseToFeatureData(response: ActiveRestrictionsLayerPickFeatureResponse): ActiveRestrictionsLayerFeatureData[] {
        return response.map((feature) => ({
            type: feature.properties.type,
            geozone: feature.properties.geozone,
            designator: feature.properties.designator,
            created: new Date(feature.properties.created),
            startTime: new Date(feature.properties.start_time),
            endTime: new Date(feature.properties.end_time),
            isStatic: feature.properties.is_static,
            lowerLimit: feature.properties.lower_limit,
            upperLimit: feature.properties.upper_limit,
        }));
    }
}
